import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

/**
 * VerifyEmail - Handles email verification from link
 * URL: /verify-email?token=xxxxx
 *
 * After successful verification, auto-login the user
 */
function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setMessage('Invalid verification link. No token provided.');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await fetch(
        `${API_URL}/auth/verify-email?token=${encodeURIComponent(token)}`,
        { method: 'GET' }
      );

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(data.message || 'Your email has been verified successfully!');

        // If server returns access_token, save it for auto-login
        if (data.access_token) {
          localStorage.setItem('token', data.access_token);
        }
      } else {
        setStatus('error');
        setMessage(data.detail || 'Verification failed. The link may be expired.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Network error. Please try again later.');
    }
  };

  const handleGoHome = () => {
    // Check if we have a token (auto-login)
    const hasToken = localStorage.getItem('token');
    if (hasToken) {
      // Reload to refresh user state
      window.location.href = '/';
    } else {
      navigate('/');
    }
  };

  const handleSignIn = () => {
    navigate('/');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('openAuthModal'));
    }, 100);
  };

  // Verifying state
  if (status === 'verifying') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Loader className="text-blue-600 animate-spin" size={32} />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-1">Verifying Your Email</h1>
          <p className="text-gray-600 text-sm">Please wait...</p>
        </div>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    const hasToken = localStorage.getItem('token');

    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="max-w-sm w-full mx-4">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-green-600" size={32} />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Email Verified! 🎉</h1>
            <p className="text-gray-600 text-sm mb-4">{message}</p>

            <button
              onClick={handleGoHome}
              className="w-full py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all"
            >
              {hasToken ? 'Continue to App' : 'Go to Homepage'}
            </button>

            {!hasToken && (
              <p className="mt-3 text-sm text-gray-500">
                <button onClick={handleSignIn} className="text-blue-600 hover:underline">
                  Sign in
                </button>
                {' '}to your account
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="max-w-sm w-full mx-4">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="text-red-600" size={32} />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Verification Failed</h1>
          <p className="text-gray-600 text-sm mb-4">{message}</p>

          <div className="bg-gray-50 rounded-lg p-3 mb-4 text-left">
            <p className="text-xs text-gray-500">
              • Link expired (24 hours)<br/>
              • Link already used<br/>
              • Invalid link
            </p>
          </div>

          <button
            onClick={() => navigate('/')}
            className="w-full py-2.5 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-all"
          >
            Go to Homepage
          </button>

          <p className="mt-3 text-sm text-gray-500">
            Need help?{' '}
            <button onClick={handleSignIn} className="text-blue-600 hover:underline">
              Try signing in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default VerifyEmail;